/**
 * Recording control hook - handles start, stop, pause, resume
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import { useAppSelector } from "app/hooks";
import type { AppDispatch } from "app/store";
import { store } from "app/store";
import {
  selectSnackbarOpen,
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import { NON_BREAKABLE_SPACE } from "constants/common";
import { selectCurrentClinic } from "features/auth/auth-slice";
import {
  appInstanceId,
  dispatchTabSyncMessage
} from "hooks/use-sync-between-tabs.hook";
import { ambientScribeApi } from "services/ambient-scribe";
import { setFavicon } from "utils/set-favicon.util";

import { AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS } from "../../components/medical-scribe-silence-warning-dialog.component";
import { createLogger } from "../../logging.util";
import {
  ERROR_MESSAGES,
  FAVICON_PATHS,
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_PAUSED,
  RECORDING_STATE_SAVING,
  RECORDING_STATE_STOPPED,
  TAB_SYNC_ACTIONS
} from "../../medical-scribe-constants";
import {
  pauseScribeRecording,
  resumeScribeRecording,
  savingInProgressScribeRecording,
  selectMedicalScribeDeviceSettings,
  selectMedicalScribeRecordingState,
  setStreamingAuthenticated,
  setStreamingConnecting,
  setStreamingError,
  setStreamingPaused,
  setStreamingReconnecting,
  setStreamingScribeId,
  setStreamingTranscript,
  startScribeRecording,
  stopScribeRecording
} from "../../medical-scribe.slice";
import type { TranscriptEntry } from "../../medical-scribe.slice";
import { createMicrophoneFingerprint } from "../../microphone.utils";
import type { MicrophoneInput } from "../../microphone.utils";
import {
  clearRecordingSession,
  getRecordingSession,
  saveRecordingSession,
  updateRecordingSessionState
} from "../../recording-session.util";
import {
  BACKGROUND_CLEANUP_WAIT_MS,
  END_RECORDING_WAIT_MS,
  PAUSE_CHUNK_SAVE_TIMEOUT_MS,
  PAUSE_FLUSH_TIMEOUT_MS,
  PAUSE_MESSAGE_DELAY_MS,
  PAUSE_WS_BUFFER_DRAIN_MS,
  RESUME_BEFORE_END_DELAY_MS,
  RESUME_CONFIRMATION_TIMEOUT_MS,
  STOP_CLEANUP_TIMEOUT_MS,
  STOP_SAVE_RECORDING_TIMEOUT_MS,
  WAIT_FOR_ALL_CHUNKS_TIMEOUT_MS,
  WS_POST_AUDIO_SAFETY_DELAY_MS,
  WS_READY_DELAY_MS,
  WS_SEND_BUFFER_DRAIN_MS
} from "../constants";
import {
  type ResumePausedScribeResult,
  getRecordingOperationBusyReason,
  isStopSuperseded,
  waitForBackgroundCleanupIdle,
  waitForRecordingOperationIdle
} from "../resume-guards.util";
import type {
  AudioRefs,
  RecordingStateRefs,
  StartRecordingInternalOptions,
  StartRecordingParams,
  WebSocketRefs
} from "../types";
import { calculateDuration, wait, withTimeout } from "../utils";

export type FinalizePauseOptions = {
  showError?: boolean;
  reason?: string;
  skipWsClose?: boolean;
};

interface UseRecordingControlProps {
  dispatch: AppDispatch;
  wsRefs: WebSocketRefs;
  audioRefs: AudioRefs;
  stateRefs: RecordingStateRefs;
  lastInterimRef: React.MutableRefObject<TranscriptEntry | null>;
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>;
  audioWorkerRef: React.MutableRefObject<any>;
  audioRecordingIdRef: React.MutableRefObject<string | null>;
  connectWebSocket: () => Promise<WebSocket>;
  authenticate: (ws: WebSocket) => Promise<void>;
  setupAudioPipeline: (
    stream: MediaStream,
    needsNewSource: boolean
  ) => Promise<void>;
  getMicrophoneStream: () => Promise<{
    stream: MediaStream;
    needsNewStream: boolean;
  }>;
  cleanupResources: () => void | Promise<void>;
  teardownAudioNodes: () => Promise<void>;
  pauseAudioGraph: () => Promise<void>;
  pendingChunkSavePromiseRef: React.MutableRefObject<Promise<true> | null>;
  startVolumeAnalysis: () => void;
  stopVolumeAnalysis: () => void;
  startChunkSaving: () => void;
  stopChunkSaving: () => Promise<true>;
  stopMediaStream: () => void;
  flushAudioWorklet: () => Promise<true>;
  isVerifiedMicrophonesEnabled: boolean;
  microphoneFingerprintRef: React.MutableRefObject<MicrophoneInput | undefined>;
  finalizePauseRef?: React.MutableRefObject<
    ((options?: FinalizePauseOptions) => Promise<void>) | null
  >;
  closeWebSocketRef?: React.MutableRefObject<
    ((reason: string) => Promise<void>) | null
  >;
  resumeInFlightRef?: React.MutableRefObject<boolean>;
}

export const useRecordingControl = ({
  dispatch,
  wsRefs,
  audioRefs,
  stateRefs,
  lastInterimRef,
  mediaRecorderRef,
  audioWorkerRef,
  audioRecordingIdRef,
  connectWebSocket,
  authenticate,
  setupAudioPipeline,
  getMicrophoneStream,
  cleanupResources,
  teardownAudioNodes,
  pauseAudioGraph,
  pendingChunkSavePromiseRef,
  startVolumeAnalysis,
  stopVolumeAnalysis,
  startChunkSaving,
  stopChunkSaving,
  stopMediaStream,
  flushAudioWorklet,
  isVerifiedMicrophonesEnabled,
  microphoneFingerprintRef,
  finalizePauseRef,
  closeWebSocketRef,
  resumeInFlightRef: externalResumeInFlightRef
}: UseRecordingControlProps) => {
  const settings = useAppSelector(selectMedicalScribeDeviceSettings);
  const currentClinic = useAppSelector(selectCurrentClinic);

  // Stable ref so callbacks don't close over stale recording state
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);
  const recordingStateRef = useRef(recordingState);
  recordingStateRef.current = recordingState;
  const pauseFinalizedRef = useRef(false);
  /** Bumped on resume/stop/new pause to cancel in-flight background pause cleanup */
  const pauseCleanupGenerationRef = useRef(0);
  /**
   * Set to true only after captureAudioBeforeBackendSignal succeeds (worklet flushed).
   * Used by stopRecording to decide whether to skip its own flush: true means the pause
   * path already drained the worklet buffer; false means stop must flush itself.
   * Chunk-save tracking has moved to pendingChunkSavePromiseRef.
   */
  const pauseFlushCompletedRef = useRef(false);
  /** True while runPauseBackgroundCleanup or runStopBackgroundCleanup is running */
  const [backgroundCleanupInFlight, setBackgroundCleanupInFlight] =
    useState(false);
  const backgroundCleanupInFlightRef = useRef(false);
  backgroundCleanupInFlightRef.current = backgroundCleanupInFlight;
  const localResumeInFlightRef = useRef(false);
  const resumeInFlightRef = externalResumeInFlightRef ?? localResumeInFlightRef;

  const cancelPendingAutoReconnect = useCallback(() => {
    if (wsRefs.reconnectTimeout.current) {
      clearTimeout(wsRefs.reconnectTimeout.current);
      wsRefs.reconnectTimeout.current = null;
    }
    wsRefs.reconnectAttempts.current = 0;
    wsRefs.reconnectDelay.current = 2000;
    dispatch(setStreamingReconnecting(false));
  }, [dispatch, wsRefs]);

  /**
   * Shared pre-start gate for Capture Visit and Resume from paused dialog.
   * Resets flush ref, tears down stale audio nodes, and awaits any in-flight
   * chunk save with a 2s cap so we don't race IDB writes.
   *
   * FE-1: teardown is awaited (was fire-and-forget). Otherwise the old
   * AudioContext.close() could race the new build in setupAudioPipeline and end
   * up closing the freshly created context. Used only by fresh-start paths
   * (Capture Visit, resumePausedScribe) — the in-widget resumeRecording reuses
   * the live context instead (FE-4) and must NOT call this.
   */
  const prepareForStart = useCallback(async () => {
    pauseFlushCompletedRef.current = false;
    await teardownAudioNodes();
    if (pendingChunkSavePromiseRef.current) {
      await Promise.race([pendingChunkSavePromiseRef.current, wait(2000)]);
    }
  }, [teardownAudioNodes, pendingChunkSavePromiseRef]);

  // Guard-reject path only: resumeRecording calls this when startRecordingInternal
  // returns false before entering its try block. Try-path failures use the catch
  // block in startRecordingInternal (snackbar + paused state, no tab sync).
  const rollbackWidgetResume = useCallback(async () => {
    // Restart pause timer so calculateDuration counts the time since the failed
    // resume as paused rather than active. resumeRecording already cleared
    // pauseStartTime before calling startRecordingInternal, so without this the
    // ongoing pause would be treated as active recording time.
    stateRefs.pauseStartTime.current = Date.now();
    setFavicon(FAVICON_PATHS.PAUSED);
    await updateRecordingSessionState(RECORDING_STATE_PAUSED);
    dispatch(setStreamingPaused(true));
    dispatch(pauseScribeRecording());
    dispatchTabSyncMessage({
      action: TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
      emitterId: appInstanceId
    });
  }, [dispatch, stateRefs]);

  /**
   * Internal function to start recording (can be called from reconnection logic)
   */
  const startRecordingInternal = useCallback(
    async (
      patientId: number,
      chartNoteId: number | undefined,
      options: StartRecordingInternalOptions = {}
    ): Promise<boolean> => {
      const {
        isReconnection = false,
        microphoneFingerprint,
        suppressSavingUi = false
      } = options;

      // Guard against concurrent operations
      if (stateRefs.isStopping.current) {
        createLogger.warn(
          "⚠️ Stop in progress, cannot start recording",
          stateRefs.recordingStartTime.current
        );
        dispatch(setStreamingError("Stop in progress, please wait"));
        return false;
      }

      if (stateRefs.isPausing.current && !isReconnection) {
        createLogger.warn(
          "⚠️ Pause in progress, cannot start recording",
          stateRefs.recordingStartTime.current
        );
        dispatch(setStreamingError("Pause in progress, please wait"));
        return false;
      }

      if (
        stateRefs.isRecording.current &&
        !stateRefs.isConnecting.current &&
        !isReconnection
      ) {
        createLogger.warn(
          "⚠️ Recording already in progress",
          stateRefs.recordingStartTime.current
        );
        dispatch(setStreamingError("Recording is already in progress"));
        return false;
      }

      if (stateRefs.isConnecting.current) {
        createLogger.warn(
          "⚠️ Recording start already in progress, skipping duplicate call",
          stateRefs.recordingStartTime.current
        );
        dispatch(setStreamingError("Connection in progress, please wait"));
        return false;
      }

      // Cancel in-flight stop/pause background cleanup from a prior session
      pauseCleanupGenerationRef.current += 1;

      stateRefs.isConnecting.current = true;
      pauseFinalizedRef.current = false;
      createLogger.log(
        "🎤 Starting recording...",
        stateRefs.recordingStartTime.current
      );

      dispatch(setStreamingError(undefined));
      dispatch(setStreamingConnecting(true));

      if (!suppressSavingUi) {
        dispatch(savingInProgressScribeRecording());
      }

      try {
        // Always sync params — reconnection (pause resume, paused scribe resume) needs them
        // for WS auto-reconnect, end_recording, and the audio worker.
        stateRefs.recordingParams.current = { patientId, chartNoteId };

        if (!isReconnection) {
          dispatch(setStreamingTranscript([]));
          lastInterimRef.current = null;
          // Reset pause tracking for new recording
          stateRefs.pauseStartTime.current = null;
          stateRefs.totalPausedDuration.current = 0;
          // Clear any previous recording ID to start fresh
          audioRecordingIdRef.current = null;
          // A prior session's pause capture must not cause stop to skip its flush
          pauseFlushCompletedRef.current = false;
        }

        // Step 1: Get microphone access
        const { stream, needsNewStream } = await getMicrophoneStream();
        const rebuildAudioPipeline = needsNewStream;

        // Step 2: Connect to WebSocket
        // Authentication is sent immediately upon connection in createWebSocketConnection
        const ws = await connectWebSocket();

        // Verify WebSocket is still open
        if (ws.readyState !== WebSocket.OPEN) {
          throw new Error(
            `WebSocket closed before authentication (state: ${ws.readyState})`
          );
        }

        // Step 3: Authenticate (may have already been sent, but wait for response)
        await authenticate(ws);

        // Step 4: Set up audio processing
        await setupAudioPipeline(stream, rebuildAudioPipeline);

        // Step 5: Send start_recording message with microphone data.
        // Priority: (1) fingerprint threaded from prepareForAmbientScribeStart (already resolved
        //     the correct active device, including Firefox private-mode activeDeviceId fallback),
        // (2) last-resort re-enumeration via stored settings.microphone (may miss rotated device IDs).
        let microphonePayload: MicrophoneInput | undefined;
        if (isVerifiedMicrophonesEnabled) {
          if (microphoneFingerprint) {
            microphonePayload = microphoneFingerprint;
          } else {
            try {
              const devices = await navigator.mediaDevices.enumerateDevices();
              const device = devices.find(
                (d) =>
                  d.kind === "audioinput" && d.deviceId === settings.microphone
              );
              if (device) {
                const fp = createMicrophoneFingerprint(device);
                microphonePayload = {
                  device_id: fp.deviceId,
                  group_id: fp.groupId,
                  label: fp.label,
                  normalized_label: fp.normalizedLabel,
                  category: fp.category,
                  user_agent: fp.userAgent
                };
              }
            } catch {
              // enumerateDevices can fail (permissions revoked mid-session); continue without mic data
            }
          }
        }

        // Update the fingerprint ref with the resolved payload so sendContinueRecording
        // can include it. On auto-reconnection (WebSocket drop) microphoneFingerprint is
        // undefined — preserve the existing ref so the ref retains the original fingerprint.
        // "||" keeps existing ref value when the new payload is undefined (reconnection case).
        if (microphonePayload !== undefined || !isReconnection) {
          microphoneFingerprintRef.current = microphonePayload;
        }

        const startRecordingMessage: {
          type: string;
          scribe_id?: number;
          patient_id: number;
          chart_note_id?: number;
          clinic_id?: number;
          microphone?: MicrophoneInput;
        } & Partial<typeof AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS> = {
          type: "start_recording",
          patient_id: patientId,
          clinic_id: currentClinic?.id,
          ...AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS,
          ...(microphonePayload && { microphone: microphonePayload })
        };

        // Add scribe_id if resuming (use ref to get latest value)
        const currentScribeId = stateRefs.scribeId.current;
        if (isReconnection && currentScribeId) {
          startRecordingMessage.scribe_id = currentScribeId;
          createLogger.log(
            `📝 Resuming recording with scribe_id: ${currentScribeId}`,
            stateRefs.recordingStartTime.current
          );
        }

        // Add optional chart_note_id
        if (chartNoteId && chartNoteId > 0) {
          startRecordingMessage.chart_note_id = chartNoteId;
        }

        // C4: guard — stop may have fired while we were awaiting mic/WS/auth/pipeline.
        // Close the WS here so the backend never receives start_recording; the stop
        // flow will see the WS already closed and skip its own close gracefully.
        if (stateRefs.isStopping.current) {
          createLogger.warn(
            "⚠️ Stop fired before start_recording send — aborting",
            stateRefs.recordingStartTime.current
          );
          ws.close(1000, "Stop before start");
          return false;
        }

        ws.send(JSON.stringify(startRecordingMessage));
        createLogger.log(
          "📤 Sent start_recording message (waiting for recording_started confirmation)",
          stateRefs.recordingStartTime.current
        );

        // Change favicon to recording and save session to IndexDB
        if (!isReconnection) {
          setFavicon(FAVICON_PATHS.RECORDING);

          // Save recording session to IndexedDB for recovery after page reload
          await saveRecordingSession({
            recordingId: `streaming_recording_${Date.now()}`,
            patientId: patientId || 0,
            patientFullName: undefined, // Streaming doesn't have patient name readily available
            chartNoteId: chartNoteId,
            recordingState: RECORDING_STATE_IN_PROGRESS,
            startTime: Date.now(),
            tabInstanceId: appInstanceId
          });

          // Broadcast to other tabs that recording started on this tab
          dispatchTabSyncMessage({
            action: TAB_SYNC_ACTIONS.START_SCRIBE_RECORDING_ON_TAB,
            emitterId: appInstanceId
          });

          createLogger.log(
            "✅ Favicon changed to recording, session saved to IndexDB, and message broadcast to other tabs",
            stateRefs.recordingStartTime.current
          );
        }

        // NOTE: Recording state will change to IN_PROGRESS when "recording_started"
        // message is received from the backend (handled in WebSocket hook)
        // Volume analysis will also start at that time
        return true;
      } catch (err) {
        createLogger.error(
          "❌ Error starting recording:",
          err,
          stateRefs.recordingStartTime.current
        );
        const errorMessage =
          err instanceof Error
            ? err.message || "Failed to start recording."
            : err instanceof DOMException
              ? `Microphone access denied or unavailable: ${err.message}`
              : "Failed to start recording. Please check your microphone permissions and try again.";

        stateRefs.isRecording.current = false;
        dispatch(setStreamingError(errorMessage));
        dispatch(setStreamingConnecting(false));
        dispatch(setStreamingReconnecting(false));

        if (isReconnection) {
          // This is a failed reconnection attempt
          // Transition to paused state so user can manually retry
          createLogger.log(
            "⚠️ Reconnection attempt failed, transitioning to paused state",
            stateRefs.recordingStartTime.current
          );

          dispatch(setStreamingPaused(true));
          dispatch(pauseScribeRecording());
          setFavicon(FAVICON_PATHS.PAUSED);

          // Show user notification
          dispatch(
            setCustomMessage(ERROR_MESSAGES.RECORDING_CONNECTION_FAILED)
          );
          dispatch(setOpen(10000));

          // Update session state to paused
          try {
            await updateRecordingSessionState(RECORDING_STATE_PAUSED);
          } catch (sessionErr) {
            createLogger.error(
              "❌ Error updating session to paused after reconnection failure:",
              sessionErr,
              stateRefs.recordingStartTime.current
            );
          }

          // Don't clean up resources - keep scribe_id and params for retry
          // User can click Resume to try again
        } else {
          // This is a failed initial start
          // Clean up everything
          setFavicon(FAVICON_PATHS.DEFAULT);
          await clearRecordingSession();

          // Reset recording state to stopped
          dispatch(stopScribeRecording(""));

          // Show user notification
          dispatch(
            setCustomMessage(ERROR_MESSAGES.RECORDING_CONNECTION_FAILED)
          );
          dispatch(setOpen(10000));

          // Broadcast stop to other tabs since we failed
          dispatchTabSyncMessage({
            action: TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB,
            emitterId: appInstanceId
          });

          await cleanupResources();
        }
        return false;
      } finally {
        stateRefs.isConnecting.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      connectWebSocket,
      authenticate,
      setupAudioPipeline,
      getMicrophoneStream,
      cleanupResources,
      currentClinic?.id,
      isVerifiedMicrophonesEnabled
      // Note: wsRefs, stateRefs, lastInterimRef, and mediaRecorderRef are refs
      // used for mutable state that doesn't trigger re-renders. They have stable
      // identity and are intentionally excluded from dependencies.
    ]
  );

  /**
   * Public start recording function
   */
  const startRecording = useCallback(
    async (params: StartRecordingParams) => {
      const { patientId, chartNoteId, microphoneFingerprint } = params;

      // Validate inputs
      if (!patientId || patientId <= 0) {
        dispatch(
          setStreamingError(
            "Patient ID is required and must be a positive number"
          )
        );
        return;
      }

      await prepareForStart();

      const isReconnection = Boolean(stateRefs.scribeId.current);
      await startRecordingInternal(patientId, chartNoteId, {
        isReconnection,
        microphoneFingerprint
      });
    },
    [dispatch, startRecordingInternal, stateRefs, prepareForStart]
  );

  /**
   * Close the WebSocket connection gracefully
   */
  const closeWebSocket = useCallback(
    async (reason: string) => {
      if (!wsRefs.ws.current) {
        return;
      }

      const ws = wsRefs.ws.current;
      try {
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => {
              createLogger.warn(
                `⚠️ WebSocket close timeout (${reason}), continuing...`,
                stateRefs.recordingStartTime.current
              );
              resolve();
            }, 2000);

            const handleClose = () => {
              clearTimeout(timeout);
              ws.removeEventListener("close", handleClose);
              resolve();
            };

            ws.addEventListener("close", handleClose);

            try {
              ws.close(1000, reason);
            } catch (closeErr) {
              createLogger.error(
                "⚠️ Error closing WebSocket",
                closeErr,
                stateRefs.recordingStartTime.current
              );
              clearTimeout(timeout);
              resolve();
            }
          });
        }
      } catch (err) {
        createLogger.error(
          "⚠️ Error in WebSocket close handling",
          err,
          stateRefs.recordingStartTime.current
        );
      }
      if (wsRefs.ws.current === ws) {
        wsRefs.ws.current = null;
      }
    },
    [wsRefs, stateRefs]
  );

  useLayoutEffect(() => {
    if (closeWebSocketRef) {
      closeWebSocketRef.current = closeWebSocket;
    }
  }, [closeWebSocket, closeWebSocketRef]);

  /**
   * Wait for the browser WebSocket send queue to drain so PCM reaches the server.
   */
  const drainWebSocketSendBuffer = useCallback(
    async (maxWaitMs: number) => {
      const currentWs = wsRefs.ws.current;
      if (!currentWs || currentWs.readyState !== WebSocket.OPEN) {
        return;
      }

      const initialBufferedAmount = currentWs.bufferedAmount;
      if (initialBufferedAmount <= 0) {
        return;
      }

      createLogger.log(
        `⏳ Waiting for ${initialBufferedAmount} bytes of audio to be transmitted (max ${maxWaitMs}ms)...`,
        stateRefs.recordingStartTime.current
      );

      const startWait = Date.now();
      while (
        currentWs.bufferedAmount > 0 &&
        Date.now() - startWait < maxWaitMs
      ) {
        await wait(100);
      }

      if (currentWs.bufferedAmount > 0) {
        createLogger.warn(
          `⚠️ WebSocket buffer still has ${currentWs.bufferedAmount} bytes after ${maxWaitMs}ms`,
          stateRefs.recordingStartTime.current
        );
      } else {
        createLogger.log(
          "✅ WebSocket send buffer drained",
          stateRefs.recordingStartTime.current
        );
      }
    },
    [stateRefs, wsRefs]
  );

  /**
   * Flush worklet buffer and persist local chunks before pause/stop signals.
   * Keeps isRecording true so late worklet messages are still captured.
   */
  const captureAudioBeforeBackendSignal = useCallback(
    async (generation: number): Promise<boolean> => {
      if (pauseCleanupGenerationRef.current !== generation) {
        return false;
      }

      const flushResult = await withTimeout(
        flushAudioWorklet(),
        PAUSE_FLUSH_TIMEOUT_MS
      );
      if (flushResult === undefined) {
        createLogger.warn(
          "⚠️ flushAudioWorklet timed out before pause/stop signal",
          stateRefs.recordingStartTime.current
        );
      }

      if (pauseCleanupGenerationRef.current !== generation) {
        return false;
      }

      // IDB save runs in the background — it does NOT block the pause/stop signal.
      // Audio already reached the backend via WebSocket during the flush above.
      // IndexedDB is a local backup only; blocking on it would make the user wait
      // for disk I/O before they see "Paused", which is a priority inversion.
      // stopMediaStream() was already called before this function, so the buffer
      // is finite and the background save completes in well under a second.
      void stopChunkSaving().then(() => {
        createLogger.log(
          "💾 Background chunk save complete",
          stateRefs.recordingStartTime.current
        );
      });

      return pauseCleanupGenerationRef.current === generation;
    },
    [flushAudioWorklet, stateRefs, stopChunkSaving]
  );

  /**
   * Idempotent pause completion: close WS, update Redux/session/favicon.
   * Shared by pauseRecording, recording_paused handler, and error/timeout fallback.
   */
  const finalizePause = useCallback(
    async (options?: FinalizePauseOptions) => {
      if (pauseFinalizedRef.current) {
        // e.g. recording_paused arrived first — still leave SAVING → Paused
        dispatch(setStreamingAuthenticated(false));
        dispatch(setStreamingReconnecting(false));
        dispatch(setStreamingPaused(true));
        dispatch(pauseScribeRecording());
        setFavicon(FAVICON_PATHS.PAUSED);
        return;
      }
      pauseFinalizedRef.current = true;

      stateRefs.isRecording.current = false;

      dispatch(setStreamingAuthenticated(false));
      dispatch(setStreamingReconnecting(false));
      dispatch(setStreamingPaused(true));
      dispatch(pauseScribeRecording());
      setFavicon(FAVICON_PATHS.PAUSED);

      dispatchTabSyncMessage({
        action: TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
        emitterId: appInstanceId
      });

      if (!options?.skipWsClose) {
        void closeWebSocket("Paused by user").catch((err: unknown) => {
          createLogger.error(
            "⚠️ Error closing WebSocket during finalizePause",
            err,
            stateRefs.recordingStartTime.current
          );
        });
      }

      void updateRecordingSessionState(RECORDING_STATE_PAUSED).catch(
        (err: unknown) => {
          createLogger.error(
            "❌ Error updating session state on pause:",
            err,
            stateRefs.recordingStartTime.current
          );
        }
      );

      if (options?.showError) {
        const message =
          options.reason === "timeout"
            ? "Pause took too long. Recording is paused — you can Resume or Stop."
            : "Failed to pause completely. Recording is paused — you can Resume or Stop.";
        dispatch(setCustomMessage(message));
        dispatch(setOpen(8000));
      }

      createLogger.log(
        "✅ Recording paused (finalizePause complete)",
        stateRefs.recordingStartTime.current
      );
    },
    [closeWebSocket, dispatch, stateRefs]
  );

  useLayoutEffect(() => {
    if (finalizePauseRef) {
      finalizePauseRef.current = finalizePause;
    }
  }, [finalizePause, finalizePauseRef]);

  /**
   * IndexedDB worker bookkeeping after pause (audio already flushed on critical path).
   */
  const runPauseBackgroundCleanup = useCallback(
    async (generation: number) => {
      if (pauseCleanupGenerationRef.current !== generation) {
        return;
      }

      setBackgroundCleanupInFlight(true);
      try {
        if (audioWorkerRef.current) {
          try {
            await audioWorkerRef.current.pauseRecording();
          } catch (err) {
            createLogger.error(
              "⚠️ Worker pause failed (background)",
              err,
              stateRefs.recordingStartTime.current
            );
          }
          await Promise.race([
            audioWorkerRef.current.waitForAllChunks(),
            wait(WAIT_FOR_ALL_CHUNKS_TIMEOUT_MS)
          ]);
          if (pauseCleanupGenerationRef.current !== generation) {
            return;
          }
        }

        if (pauseCleanupGenerationRef.current !== generation) {
          return;
        }

        createLogger.log(
          "💾 Pause background cleanup complete",
          stateRefs.recordingStartTime.current
        );
      } catch (err) {
        createLogger.error(
          "⚠️ Pause background cleanup failed (recording is already paused)",
          err,
          stateRefs.recordingStartTime.current
        );
      } finally {
        setBackgroundCleanupInFlight(false);
      }
    },
    [audioWorkerRef, stateRefs]
  );

  /**
   * IndexedDB save + audio/WS teardown after stop UI has reset.
   * Skipped when a newer pause/stop/start bumps pauseCleanupGenerationRef.
   */
  const runStopBackgroundCleanup = useCallback(
    async (
      generation: number,
      capturedAudioRecordingId: string | null | undefined,
      capturedScribeIdForLink: number | undefined,
      wasStoppingFromPaused: boolean
    ) => {
      if (pauseCleanupGenerationRef.current !== generation) {
        return;
      }

      setBackgroundCleanupInFlight(true);
      try {
        // When stopping from paused, captureAudioBeforeBackendSignal already started
        // stopChunkSaving. Await that promise before saveRecording to avoid a concurrent
        // addChunk + saveRecording race over shared IDB state (FE-0 / FE-2).
        if (wasStoppingFromPaused && pendingChunkSavePromiseRef.current) {
          await Promise.race([pendingChunkSavePromiseRef.current, wait(2000)]);
          if (pauseCleanupGenerationRef.current !== generation) {
            return;
          }
        }

        if (audioWorkerRef.current && capturedAudioRecordingId) {
          try {
            const result = await withTimeout(
              audioWorkerRef.current.saveRecording(),
              STOP_SAVE_RECORDING_TIMEOUT_MS
            );
            if (pauseCleanupGenerationRef.current !== generation) {
              return;
            }
            createLogger.log(
              `💾 Audio recording saved: ${(result as { chunks?: unknown[] })?.chunks?.length || 0} chunks`,
              stateRefs.recordingStartTime.current
            );

            if (capturedScribeIdForLink) {
              try {
                const { getDB } = await import(
                  "../../medical-scribe-db/medical-scribe-db.util"
                );
                const db = await getDB();
                const tx = db.transaction("recordings", "readwrite");
                const idbStore = tx.objectStore("recordings");
                const recording = await idbStore.get(capturedAudioRecordingId);

                if (recording) {
                  recording.medical_transcribe_id = capturedScribeIdForLink;
                  await idbStore.put(recording);
                  await tx.done;
                  createLogger.log(
                    `✅ Linked audio recording ${capturedAudioRecordingId} to scribe ID ${capturedScribeIdForLink}`,
                    stateRefs.recordingStartTime.current
                  );
                }
              } catch (err) {
                createLogger.error(
                  "❌ Error linking audio recording to scribe ID:",
                  err,
                  stateRefs.recordingStartTime.current
                );
              }
            }
          } catch (err) {
            createLogger.error(
              "❌ Error saving audio recording:",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        }

        if (pauseCleanupGenerationRef.current !== generation) {
          return;
        }

        if (wasStoppingFromPaused) {
          // Pause path already ran stopChunkSaving; close WS if still open then teardown nodes (FE-2)
          void closeWebSocket("Recording stopped from paused").catch(
            (err: unknown) => {
              createLogger.error(
                "❌ Error closing WS after stop-from-paused:",
                err,
                stateRefs.recordingStartTime.current
              );
            }
          );
          try {
            await teardownAudioNodes();
          } catch (err) {
            createLogger.error(
              "❌ Error during audio node teardown after stop-from-paused:",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        } else {
          try {
            const cleanupResult = await withTimeout(
              Promise.resolve(cleanupResources()),
              STOP_CLEANUP_TIMEOUT_MS
            );
            if (cleanupResult === undefined) {
              createLogger.warn(
                "⚠️ Stop cleanup timed out (new recording may proceed)",
                stateRefs.recordingStartTime.current
              );
            }
          } catch (err) {
            createLogger.error(
              "❌ Error during stop cleanup:",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        }

        if (pauseCleanupGenerationRef.current !== generation) {
          return;
        }

        try {
          await clearRecordingSession();
        } catch (err) {
          createLogger.error(
            "❌ Error clearing recording session after stop:",
            err,
            stateRefs.recordingStartTime.current
          );
        }

        dispatchTabSyncMessage({
          action: TAB_SYNC_ACTIONS.STOP_SCRIBE_RECORDING_ON_TAB,
          emitterId: appInstanceId
        });

        createLogger.log(
          "✅ Stop background cleanup complete",
          stateRefs.recordingStartTime.current
        );
      } finally {
        setBackgroundCleanupInFlight(false);
      }
    },
    [
      audioWorkerRef,
      cleanupResources,
      closeWebSocket,
      pendingChunkSavePromiseRef,
      stateRefs,
      teardownAudioNodes
    ]
  );

  type PauseRecordingOptions = {
    /** Stop dialog pauses without SAVING so Cancel can resume immediately */
    silent?: boolean;
  };

  const revertInProgressPauseUi = useCallback(() => {
    if (pauseFinalizedRef.current) {
      return;
    }
    stateRefs.isRecording.current = true;
    stateRefs.pauseStartTime.current = null;
    dispatch(setStreamingPaused(false));
    dispatch(resumeScribeRecording());
  }, [dispatch, stateRefs]);

  /**
   * Pause recording — flush audio to server, then pause message + UI update.
   */
  const pauseRecording = useCallback(
    async (options?: PauseRecordingOptions) => {
      if (stateRefs.isPausing.current) {
        createLogger.warn(
          "⚠️ Pause already in progress, skipping duplicate call",
          stateRefs.recordingStartTime.current
        );
        return;
      }

      if (stateRefs.isStopping.current) {
        createLogger.warn(
          "⚠️ Stop in progress, cannot pause",
          stateRefs.recordingStartTime.current
        );
        return;
      }

      if (!stateRefs.isRecording.current) {
        createLogger.warn(
          "⚠️ Cannot pause: not currently recording",
          stateRefs.recordingStartTime.current
        );
        return;
      }

      const generation = pauseCleanupGenerationRef.current + 1;
      pauseCleanupGenerationRef.current = generation;

      pauseFlushCompletedRef.current = false;
      stateRefs.isPausing.current = true;
      pauseFinalizedRef.current = false;

      try {
        createLogger.log(
          "⏸️ Pausing recording...",
          stateRefs.recordingStartTime.current
        );

        if (!options?.silent) {
          dispatch(savingInProgressScribeRecording());
        }

        stateRefs.pauseStartTime.current = Date.now();

        cancelPendingAutoReconnect();

        stopVolumeAnalysis();

        // Stop the media stream before flush/save so no new audio arrives while
        // the worklet ring buffer and audio buffer are being drained. Mirrors the
        // stop flow's ordering (stopMediaStream → wait → flush → stopChunkSaving).
        // Without this, stopChunkSaving keeps finding new data and always times out.
        stopMediaStream();
        await wait(300);

        const captured = await captureAudioBeforeBackendSignal(generation);
        if (!captured) {
          // Reset pauseStartTime regardless of silent/non-silent: the pause was
          // cancelled (generation mismatch), so pauseStartTime should not be
          // treated as an ongoing pause by calculateDuration.
          stateRefs.pauseStartTime.current = null;
          if (
            pauseCleanupGenerationRef.current === generation &&
            !options?.silent
          ) {
            revertInProgressPauseUi();
          }
          return;
        }

        // Worklet has been flushed and chunk saving started. Signal stop that it
        // can skip its own flush — the buffer has already been drained.
        pauseFlushCompletedRef.current = true;

        stateRefs.isRecording.current = false;

        // FE-4: suspend the context + drop the stale mic source, but keep the
        // AudioContext + worklet alive so the next resume reuses them (one context
        // per visit = stable resample ratio, no leak). Stop/Postpone tear it down.
        await pauseAudioGraph();

        await drainWebSocketSendBuffer(PAUSE_WS_BUFFER_DRAIN_MS);
        if (pauseCleanupGenerationRef.current !== generation) {
          return;
        }

        if (
          wsRefs.ws.current &&
          wsRefs.ws.current.readyState === WebSocket.OPEN
        ) {
          try {
            wsRefs.ws.current.send(JSON.stringify({ type: "pause" }));
            createLogger.log(
              "📤 Sent pause message to backend",
              stateRefs.recordingStartTime.current
            );
            await wait(PAUSE_MESSAGE_DELAY_MS);
          } catch (err) {
            createLogger.error(
              "⚠️ Error sending pause message",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        }

        await finalizePause();
        void runPauseBackgroundCleanup(generation);
      } catch (err) {
        createLogger.error(
          "❌ Pause failed, forcing paused state",
          err,
          stateRefs.recordingStartTime.current
        );
        await finalizePause({
          showError: true,
          reason: err instanceof Error ? err.message : "unknown"
        });
        void runPauseBackgroundCleanup(generation);
      } finally {
        stateRefs.isPausing.current = false;
      }
    },
    [
      stateRefs.isPausing,
      stateRefs.isStopping,
      stateRefs.isRecording,
      stateRefs.recordingStartTime,
      stateRefs.pauseStartTime,
      cancelPendingAutoReconnect,
      stopVolumeAnalysis,
      stopMediaStream,
      captureAudioBeforeBackendSignal,
      pauseAudioGraph,
      drainWebSocketSendBuffer,
      wsRefs.ws,
      finalizePause,
      runPauseBackgroundCleanup,
      dispatch,
      revertInProgressPauseUi
    ]
  );

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(async () => {
    if (
      resumeInFlightRef.current ||
      stateRefs.isStopping.current ||
      stateRefs.isPausing.current ||
      stateRefs.isConnecting.current
    ) {
      if (resumeInFlightRef.current) {
        createLogger.warn(
          "⚠️ Resume already in progress, ignoring duplicate",
          stateRefs.recordingStartTime.current
        );
      } else if (stateRefs.isStopping.current) {
        createLogger.warn(
          "⚠️ Stop in progress, cannot resume",
          stateRefs.recordingStartTime.current
        );
      } else if (stateRefs.isPausing.current) {
        createLogger.warn(
          "⚠️ Pause in progress, cannot resume",
          stateRefs.recordingStartTime.current
        );
      } else {
        createLogger.warn(
          "⚠️ Connection in progress, cannot resume",
          stateRefs.recordingStartTime.current
        );
      }
      return;
    }

    if (!stateRefs.scribeId.current) {
      createLogger.error(
        "❌ Cannot resume: no scribe_id available",
        undefined,
        stateRefs.recordingStartTime.current
      );
      dispatch(setStreamingError("Cannot resume: no scribe_id available"));
      return;
    }

    if (!stateRefs.recordingParams.current) {
      createLogger.error(
        "❌ Cannot resume: no recording params available",
        undefined,
        stateRefs.recordingStartTime.current
      );
      dispatch(
        setStreamingError("Cannot resume: recording parameters not found")
      );
      return;
    }

    resumeInFlightRef.current = true;

    try {
      createLogger.log(
        "▶️ Resuming recording...",
        stateRefs.recordingStartTime.current
      );

      cancelPendingAutoReconnect();
      pauseCleanupGenerationRef.current += 1;

      if (stateRefs.pauseStartTime.current) {
        const pausedDuration = Date.now() - stateRefs.pauseStartTime.current;
        stateRefs.totalPausedDuration.current += pausedDuration;
        stateRefs.pauseStartTime.current = null;
        createLogger.log(
          `⏱️ Accumulated paused duration: ${pausedDuration}ms (total paused: ${stateRefs.totalPausedDuration.current}ms)`,
          stateRefs.recordingStartTime.current
        );
      }

      dispatch(setStreamingPaused(false));
      dispatch(setStreamingError(undefined));
      setFavicon(FAVICON_PATHS.RECORDING);

      await updateRecordingSessionState(RECORDING_STATE_IN_PROGRESS);

      dispatchTabSyncMessage({
        action: TAB_SYNC_ACTIONS.RESUME_SCRIBE_RECORDING_ON_TAB,
        emitterId: appInstanceId
      });

      // FE-4: intentionally NOT calling prepareForStart() here. The in-widget
      // resume reuses the AudioContext + worklet that pauseAudioGraph() kept
      // alive — setupAudioPipeline recreates only the mic source and resumes the
      // suspended context. Tearing down here would leak/rebuild the context and
      // reintroduce both pause/resume bugs.
      const started = await startRecordingInternal(
        stateRefs.recordingParams.current.patientId,
        stateRefs.recordingParams.current.chartNoteId,
        { isReconnection: true }
      );

      if (!started) {
        await rollbackWidgetResume();
        return;
      }

      createLogger.log(
        "✅ Recording resumed (favicon restored, session updated, message broadcast)",
        stateRefs.recordingStartTime.current
      );
    } finally {
      resumeInFlightRef.current = false;
    }
  }, [
    dispatch,
    stateRefs,
    startRecordingInternal,
    cancelPendingAutoReconnect,
    rollbackWidgetResume,
    resumeInFlightRef
  ]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(async () => {
    if (stateRefs.isStopping.current) {
      createLogger.warn(
        "⚠️ Stop already in progress, skipping duplicate call",
        stateRefs.recordingStartTime.current
      );
      return;
    }

    // Capture BEFORE incrementing the generation so we can detect a concurrent
    pauseCleanupGenerationRef.current += 1;
    const stopGeneration = pauseCleanupGenerationRef.current;
    stateRefs.isStopping.current = true;

    let stopUiFinalized = false;
    let stopAborted = false;

    const abortStopFromPaused = () => {
      stopAborted = true;
      stateRefs.isStopping.current = false;
      dispatch(setStreamingPaused(true));
      dispatch(pauseScribeRecording());
      setFavicon(FAVICON_PATHS.PAUSED);
      dispatch(
        setCustomMessage(
          "Could not stop — the scribe could not be loaded. Try Resume, then Stop again."
        )
      );
      dispatch(setOpen(10000));
    };

    const finalizeStopUi = () => {
      if (stopUiFinalized) {
        return;
      }
      stopUiFinalized = true;
      stateRefs.isStopping.current = false;
      stateRefs.isPausing.current = false;
      stateRefs.isConnecting.current = false; // FE-11: unblock next Capture Visit if stop fired mid-connection
      stateRefs.scribeId.current = undefined;
      mediaRecorderRef.current = null;
      audioRecordingIdRef.current = null;
      stateRefs.recordingParams.current = null;
      microphoneFingerprintRef.current = undefined;
      dispatch(setStreamingReconnecting(false));
      dispatch(setStreamingPaused(false));
      dispatch(stopScribeRecording(""));
      dispatch(ambientScribeApi.util.invalidateTags(["AmbientScribes"]));
      setFavicon(FAVICON_PATHS.DEFAULT);
      void teardownAudioNodes(); // release mic and AudioContext before slow IDB work
    };

    const abortStopIfSuperseded = (closeReconnected?: () => void): boolean => {
      if (
        !isStopSuperseded(stopGeneration, pauseCleanupGenerationRef.current)
      ) {
        return false;
      }
      stopAborted = true;
      stateRefs.isStopping.current = false;
      closeReconnected?.();
      createLogger.log(
        "⏭️ Stop aborted — superseded by Postpone/Resume",
        stateRefs.recordingStartTime.current
      );
      return true;
    };

    try {
      createLogger.log(
        "🛑 Stopping recording...",
        stateRefs.recordingStartTime.current
      );

      // Set saving state to show loading indicator
      dispatch(savingInProgressScribeRecording());

      // Calculate duration before cleanup, excluding paused time
      const duration = calculateDuration(
        stateRefs.recordingStartTime.current,
        stateRefs.totalPausedDuration.current,
        stateRefs.pauseStartTime.current
      );

      // Stop dialog pauses first, then may leave Redux on SAVING (legacy) or PAUSED.
      // pauseFinalizedRef / !isRecording mean teardown already ran on pause.
      // pauseCaptureCompletedRef: the pause path successfully flushed the worklet
      // and started chunk saving, so stop can skip a redundant second drain. Being
      // mid-pause (isPausing) is NOT sufficient — if stop fired during the pause's
      // 300ms wait, the generation bump cancelled capture and stop must flush itself.
      const stoppingFromPaused =
        pauseFlushCompletedRef.current ||
        pauseFinalizedRef.current ||
        recordingStateRef.current === RECORDING_STATE_PAUSED ||
        (recordingStateRef.current === RECORDING_STATE_SAVING &&
          !stateRefs.isRecording.current) ||
        !stateRefs.isRecording.current;

      if (stoppingFromPaused) {
        createLogger.log(
          "🛑 Stopping from paused state — skipping capture/drain (already done on pause)",
          stateRefs.recordingStartTime.current
        );
        stopMediaStream();
        stateRefs.isRecording.current = false;
        // The WS may still be open if the pause was in-flight when stop fired (the
        // silent pause is aborted before it closes the WS). Apply the same drain +
        // safety delay the active-recording path uses so the backend receives all
        // in-flight audio.
        if (wsRefs.ws.current?.readyState === WebSocket.OPEN) {
          await drainWebSocketSendBuffer(WS_SEND_BUFFER_DRAIN_MS);
          createLogger.log(
            "⏳ Safety delay for server-side audio processing before end_recording...",
            stateRefs.recordingStartTime.current
          );
          await wait(WS_POST_AUDIO_SAFETY_DELAY_MS);
        }
      } else {
        // IMPORTANT: Stop audio in the correct order to prevent data loss
        // Firefox handles AudioWorklet message timing differently than Chrome,
        // so we use longer delays to ensure all audio data is captured.

        // 1. First stop the media stream (stops new audio from entering the pipeline)
        stopMediaStream();

        // 2. Wait for any in-flight audio data to be processed
        await wait(300);

        // 3. Flush any remaining audio in the worklet's resampling buffer
        const flushResult = await withTimeout(
          flushAudioWorklet(),
          PAUSE_FLUSH_TIMEOUT_MS
        );
        if (flushResult === undefined) {
          createLogger.warn(
            "⚠️ flushAudioWorklet timed out during stop",
            stateRefs.recordingStartTime.current
          );
        }

        // 4. Wait for flushed data and any remaining messages to arrive
        await wait(200);

        // 5. Save chunks BEFORE setting isRecording to false
        const chunkResult = await withTimeout(
          stopChunkSaving(),
          PAUSE_CHUNK_SAVE_TIMEOUT_MS
        );
        if (chunkResult === undefined) {
          createLogger.warn(
            "⚠️ stopChunkSaving timed out during stop",
            stateRefs.recordingStartTime.current
          );
        }
        createLogger.log(
          "💾 All pending audio chunks saved to IndexedDB",
          stateRefs.recordingStartTime.current
        );

        // 6. NOW stop accepting new data (after saving)
        stateRefs.isRecording.current = false;

        await drainWebSocketSendBuffer(WS_SEND_BUFFER_DRAIN_MS);

        if (wsRefs.ws.current?.readyState === WebSocket.OPEN) {
          createLogger.log(
            "⏳ Safety delay for server-side audio processing before end_recording...",
            stateRefs.recordingStartTime.current
          );
          await wait(WS_POST_AUDIO_SAFETY_DELAY_MS);
        }
      }

      if (abortStopIfSuperseded()) {
        return;
      }

      // Send end_recording message if we have a scribeId (use ref to get latest value)
      const currentScribeId = stateRefs.scribeId.current;
      if (currentScribeId) {
        let ws = wsRefs.ws.current;
        let needsReconnect = false;

        const closeReconnectedStopWebSocket = () => {
          if (!needsReconnect || !ws) {
            return;
          }
          try {
            if (ws.readyState === WebSocket.OPEN) {
              ws.close(1000, "Recording stopped");
            }
          } catch (err) {
            createLogger.error(
              "⚠️ Error closing reconnected WebSocket",
              err,
              stateRefs.recordingStartTime.current
            );
          }
          if (wsRefs.ws.current === ws) {
            wsRefs.ws.current = null;
          }
        };

        // Check if WebSocket is open
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          createLogger.log(
            "🔄 WebSocket not open, reconnecting to send end_recording message...",
            stateRefs.recordingStartTime.current
          );
          needsReconnect = true;
          try {
            ws = await connectWebSocket();
            await new Promise((resolve) =>
              setTimeout(resolve, WS_READY_DELAY_MS)
            );

            if (abortStopIfSuperseded(closeReconnectedStopWebSocket)) {
              return;
            }

            if (ws.readyState === WebSocket.OPEN) {
              await authenticate(ws);

              // When stopping from paused state, we need to resume first to load the scribe
              // in the backend, then we can end it
              if (stateRefs.recordingParams.current) {
                const resumeMessage = {
                  type: "start_recording",
                  scribe_id: currentScribeId,
                  clinic_id: currentClinic?.id,
                  patient_id: stateRefs.recordingParams.current.patientId,
                  ...(stateRefs.recordingParams.current.chartNoteId && {
                    chart_note_id: stateRefs.recordingParams.current.chartNoteId
                  }),
                  ...AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS
                };
                createLogger.log(
                  "📤 Resuming recording to load scribe before ending...",
                  stateRefs.recordingStartTime.current
                );
                ws.send(JSON.stringify(resumeMessage));

                const wsForCallback = ws;

                const resumeBeforeEnd = await new Promise<{
                  ok: boolean;
                  reason?: string;
                }>((resolve) => {
                  const originalOnMessage = wsForCallback.onmessage;

                  const timeout = setTimeout(() => {
                    if (originalOnMessage) {
                      wsForCallback.onmessage = originalOnMessage;
                    }
                    createLogger.warn(
                      "⚠️ Timeout waiting for recording_started before end_recording",
                      stateRefs.recordingStartTime.current
                    );
                    resolve({ ok: false, reason: "timeout" });
                  }, RESUME_CONFIRMATION_TIMEOUT_MS);

                  wsForCallback.onmessage = (event) => {
                    try {
                      const msg = JSON.parse(event.data);
                      if (msg.type === "recording_started") {
                        clearTimeout(timeout);
                        if (originalOnMessage) {
                          wsForCallback.onmessage = originalOnMessage;
                        }
                        createLogger.log(
                          "✅ Recording resumed, now sending end_recording...",
                          stateRefs.recordingStartTime.current
                        );
                        resolve({ ok: true });
                      } else if (msg.type === "recording_error") {
                        clearTimeout(timeout);
                        if (originalOnMessage) {
                          wsForCallback.onmessage = originalOnMessage;
                        }
                        createLogger.error(
                          `❌ recording_error before end_recording: ${msg.error || "Unknown error"}`,
                          msg,
                          stateRefs.recordingStartTime.current
                        );
                        resolve({ ok: false, reason: "recording_error" });
                      } else if (originalOnMessage) {
                        originalOnMessage.call(wsForCallback, event);
                      }
                    } catch {
                      if (originalOnMessage) {
                        originalOnMessage.call(wsForCallback, event);
                      }
                    }
                  };
                });

                if (!resumeBeforeEnd.ok) {
                  closeReconnectedStopWebSocket();
                  abortStopFromPaused();
                  return;
                }

                await new Promise((resolve) =>
                  setTimeout(resolve, RESUME_BEFORE_END_DELAY_MS)
                );

                if (abortStopIfSuperseded(closeReconnectedStopWebSocket)) {
                  return;
                }
              }
            }
          } catch (err) {
            const errorMessage =
              err instanceof Error ? err.message : "Network error";

            createLogger.error(
              `❌ Failed to reconnect to send end_recording: ${errorMessage}. Recording will be ended locally but may not be properly closed on the server.`,
              err,
              stateRefs.recordingStartTime.current
            );

            // Log for user visibility - the recording will still be ended locally
            dispatch(
              setStreamingError(
                "Connection lost. Recording ended locally. Server may need manual cleanup."
              )
            );
          }
        }

        if (abortStopIfSuperseded(closeReconnectedStopWebSocket)) {
          return;
        }

        // Send end_recording message
        if (ws && ws.readyState === WebSocket.OPEN) {
          try {
            const endRecordingMessage = {
              type: "end_recording",
              duration: duration,
              find_related_chart_note: settings.autoFindRelatedChartNote,
              transform: settings.autoGenerateSOAPNotes ? "soap" : undefined
            };

            ws.send(JSON.stringify(endRecordingMessage));
            createLogger.log(
              "📤 Sent end_recording message",
              stateRefs.recordingStartTime.current
            );

            // Wait for the message to be sent
            await new Promise((resolve) =>
              setTimeout(resolve, END_RECORDING_WAIT_MS)
            );

            // Show toast message after successful upload
            // Use a ref to get the latest snackbar state without causing re-renders
            const currentSnackbarState = selectSnackbarOpen(store.getState());
            if (!currentSnackbarState) {
              // Get patient name from recording session
              let patientFullName: string | undefined;
              try {
                const session = await getRecordingSession();
                patientFullName = session?.patientFullName;
              } catch (err) {
                createLogger.warn(
                  "Failed to get recording session for patient name:",
                  stateRefs.recordingStartTime.current
                );
              }

              dispatch(
                setCustomMessage(
                  `The scribe record${
                    patientFullName ? ` for ${patientFullName}` : ""
                  } is uploaded. You receive notification when it will be${NON_BREAKABLE_SPACE}processed.`
                )
              );
              dispatch(setOpen(10000));
            }
          } catch (err) {
            createLogger.error(
              "❌ Error sending end_recording:",
              err,
              stateRefs.recordingStartTime.current
            );
          }
        }

        closeReconnectedStopWebSocket();
      }

      const capturedAudioRecordingId = audioRecordingIdRef.current;
      const capturedScribeIdForLink = stateRefs.scribeId.current;

      if (stopAborted) {
        return;
      }

      // Reset widget immediately after server notified — do not block on worker/IDB
      finalizeStopUi();

      void runStopBackgroundCleanup(
        stopGeneration,
        capturedAudioRecordingId,
        capturedScribeIdForLink,
        stoppingFromPaused
      );
    } catch (err) {
      createLogger.error(
        "❌ Error during stop recording:",
        err,
        stateRefs.recordingStartTime.current
      );
      if (!stopAborted) {
        finalizeStopUi();
      }
    } finally {
      if (!stopAborted) {
        finalizeStopUi();
        setFavicon(FAVICON_PATHS.DEFAULT);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    connectWebSocket,
    authenticate,
    cleanupResources,
    teardownAudioNodes,
    runStopBackgroundCleanup,
    mediaRecorderRef,
    settings,
    currentClinic?.id,
    stopChunkSaving,
    stopMediaStream,
    flushAudioWorklet,
    audioWorkerRef,
    audioRecordingIdRef
    // Note: wsRefs and stateRefs are refs used for mutable state that doesn't
    // trigger re-renders. They have stable identity and are intentionally excluded.
  ]);

  /**
   * Lightweight cleanup after the user clicks Postpone later.
   * The WebSocket is already closed (from the prior pause), so we skip the
   * full stopRecording() flow (media-stream teardown, WS buffer drain, etc.)
   * and only reset the refs + IndexedDB session that would otherwise leave
   * the widget in a stale-reconnection state.
   */
  const resetAfterPaused = useCallback(() => {
    pauseCleanupGenerationRef.current += 1;
    setBackgroundCleanupInFlight(false);
    stateRefs.scribeId.current = undefined;
    stateRefs.isRecording.current = false;
    stateRefs.isPausing.current = false;
    stateRefs.isConnecting.current = false;
    stateRefs.isStopping.current = false;
    pauseFinalizedRef.current = false;
    stateRefs.totalPausedDuration.current = 0;
    stateRefs.pauseStartTime.current = null;
    audioRecordingIdRef.current = null;
    mediaRecorderRef.current = null;
    setFavicon(FAVICON_PATHS.DEFAULT);
    clearRecordingSession(); // async fire-and-forget — clears IndexedDB session
    dispatch(stopScribeRecording(""));
    void closeWebSocket("Paused").catch((err: unknown) => {
      createLogger.error(
        "⚠️ Error closing WebSocket during resetAfterPaused",
        err,
        stateRefs.recordingStartTime.current
      );
    });
    void teardownAudioNodes().catch((err: unknown) => {
      createLogger.error(
        "⚠️ Error releasing audio nodes after paused save",
        err,
        stateRefs.recordingStartTime.current
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, closeWebSocket, teardownAudioNodes]);

  /**
   * Prepare Edit Scribe Resume: wait for in-flight pause/stop and background
   * cleanup (bounded), then detach the widget if it still owns this scribe.
   */
  const prepareForResumeFromEditScribe = useCallback(
    async (targetScribeId: number) => {
      await waitForRecordingOperationIdle({
        getFlags: () => ({
          resumeInFlight: resumeInFlightRef.current,
          isConnecting: stateRefs.isConnecting.current,
          isStopping: stateRefs.isStopping.current,
          isPausing: stateRefs.isPausing.current
        }),
        wait
      });

      await waitForBackgroundCleanupIdle({
        getBackgroundCleanupInFlight: () =>
          backgroundCleanupInFlightRef.current,
        timeoutMs: BACKGROUND_CLEANUP_WAIT_MS,
        wait
      });

      const widgetScribeId = stateRefs.scribeId.current;
      const widgetState = recordingStateRef.current;

      if (
        widgetScribeId != null &&
        widgetScribeId === targetScribeId &&
        widgetState !== RECORDING_STATE_STOPPED
      ) {
        resetAfterPaused();
      }
    },
    [resetAfterPaused, resumeInFlightRef, stateRefs]
  );

  /**
   * Resume a previously paused scribe from the "Edit Scribe" dialog.
   * Distinct from resumeRecording() which resumes the currently-active
   * paused widget session — this accepts an external scribeId.
   */
  const resumePausedScribe = useCallback(
    async (params: {
      scribeId: number;
      patientId: number;
      patientFullName?: string;
      chartNoteId?: number;
      microphoneFingerprint?: MicrophoneInput;
    }): Promise<ResumePausedScribeResult> => {
      const {
        scribeId,
        patientId,
        chartNoteId,
        microphoneFingerprint,
        patientFullName
      } = params;

      const getOperationFlags = () => ({
        resumeInFlight: resumeInFlightRef.current,
        isConnecting: stateRefs.isConnecting.current,
        isStopping: stateRefs.isStopping.current,
        isPausing: stateRefs.isPausing.current
      });

      if (!scribeId || !patientId) {
        dispatch(setStreamingError("Cannot resume: missing scribe or patient"));
        return { ok: false, reason: "missing_params" };
      }

      cancelPendingAutoReconnect();

      await waitForBackgroundCleanupIdle({
        getBackgroundCleanupInFlight: () =>
          backgroundCleanupInFlightRef.current,
        timeoutMs: BACKGROUND_CLEANUP_WAIT_MS,
        wait
      });

      pauseCleanupGenerationRef.current += 1;

      const idle = await waitForRecordingOperationIdle({
        getFlags: getOperationFlags,
        wait
      });

      if (!idle) {
        const reason =
          getRecordingOperationBusyReason(getOperationFlags()) ?? "timeout";
        createLogger.warn(
          `⚠️ Cannot resume paused scribe: ${reason}`,
          stateRefs.recordingStartTime.current
        );
        return { ok: false, reason };
      }

      if (resumeInFlightRef.current) {
        createLogger.warn(
          "⚠️ Cannot resume paused scribe: resume already in flight",
          stateRefs.recordingStartTime.current
        );
        return { ok: false, reason: "in_flight" };
      }

      resumeInFlightRef.current = true;

      try {
        pauseFinalizedRef.current = false;

        try {
          await closeWebSocket("Resume paused scribe");
          await prepareForStart(); // FE-10: was cleanupResources() which could hang 20s+
        } catch (err) {
          createLogger.error(
            "⚠️ Error preparing audio for paused resume",
            err,
            stateRefs.recordingStartTime.current
          );
        }

        stateRefs.scribeId.current = scribeId;
        stateRefs.recordingParams.current = { patientId, chartNoteId };
        audioRecordingIdRef.current = null;
        stateRefs.totalPausedDuration.current = 0;
        stateRefs.pauseStartTime.current = null;

        dispatch(setStreamingScribeId(scribeId));
        dispatch(setStreamingPaused(false));
        dispatch(setStreamingReconnecting(false));
        dispatch(setStreamingConnecting(false));
        dispatch(setStreamingError(undefined));
        // dispatch(startScribeRecording) moved after startRecordingInternal succeeds (FE-9)

        await saveRecordingSession({
          recordingId: `streaming_recording_${Date.now()}`,
          patientId,
          patientFullName,
          chartNoteId,
          scribeId,
          recordingState: RECORDING_STATE_IN_PROGRESS,
          startTime: Date.now(),
          tabInstanceId: appInstanceId
        });

        const started = await startRecordingInternal(patientId, chartNoteId, {
          isReconnection: true,
          microphoneFingerprint
        });

        if (!started) {
          void clearRecordingSession(); // clean up IDB session written above (FE-9)
          return { ok: false, reason: "start_failed" };
        }

        dispatch(startScribeRecording({ patientId, chartNoteId })); // FE-9: dispatch only on confirmed start
        return { ok: true };
      } finally {
        resumeInFlightRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      startRecordingInternal,
      closeWebSocket,
      prepareForStart,
      cancelPendingAutoReconnect,
      resumeInFlightRef
    ]
  );

  return {
    startRecording,
    startRecordingInternal,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetAfterPaused,
    resumePausedScribe,
    prepareForResumeFromEditScribe,
    backgroundCleanupInFlight
  };
};
